/**
 * End User License Agreement (EULA) Page
 *
 * v2.0 - 2026-03-23 - Refactored to use LegalPageShell; dark-mode-safe colors; legal@ email
 * v2.49 - 2026-02-03 - Complete EULA with full legal text
 */
import React from 'react';
import { Box, Container, Divider, Link, Paper, Typography } from '@mui/material';
import LegalPageShell, { LEGAL_STICKY_BAR_HEIGHT, legalBodyTextSx } from '../components/LegalPageShell';

// ─── Section registry ─────────────────────────────────────────────────────────
const SECTIONS = [
  { id: 'definitions',      label: '1. Definitions' },
  { id: 'license',          label: '2. License Grant' },
  { id: 'accounts',         label: '3. Account Registration' },
  { id: 'payment',          label: '4. Subscription and Payment' },
  { id: 'data-rights',      label: '5. Business Data and Data Rights' },
  { id: 'integrations',     label: '6. Third-Party Integrations' },
  { id: 'ai-features',      label: '7. AI-Powered Features' },
  { id: 'ip',               label: '8. Intellectual Property' },
  { id: 'confidentiality',  label: '9. Confidentiality' },
  { id: 'warranties',       label: '10. Warranties and Disclaimers' },
  { id: 'liability',        label: '11. Limitation of Liability' },
  { id: 'indemnification',  label: '12. Indemnification' },
  { id: 'termination',      label: '13. Term and Termination' },
  { id: 'dispute',          label: '14. Dispute Resolution' },
  { id: 'general',          label: '15. General Provisions' },
  { id: 'contact',          label: '16. Contact Information' },
];

const Eula: React.FC = () => (
  <LegalPageShell title="End User License Agreement" lastUpdated="March 23, 2026" sections={SECTIONS}>
    <Container maxWidth="md" sx={{ py: 6 }}>

      {/* Important Notice */}
      <Paper elevation={2} sx={{ p: 3, bgcolor: 'warning.light', mb: 4 }}>
        <Typography variant="h6" sx={{ color: 'warning.dark', fontWeight: 700, mb: 2 }}>
          IMPORTANT: PLEASE READ CAREFULLY
        </Typography>
        <Typography sx={{ color: 'warning.dark' }}>
          This End User License Agreement ("Agreement" or "EULA") is a legally binding contract between you ("User," "you," or "your") and Data Edge Services LLC, a Texas limited liability company doing business as MyLojic ("Company," "we," "us," or "our"). This Agreement governs your access to and use of the MyLojic platform, including all related applications, services, tools, dashboards, and APIs (collectively, the "Platform").
        </Typography>
        <Typography sx={{ color: 'warning.dark', mt: 2 }}>
          By accessing or using the Platform, creating an account, or clicking "I Agree," you acknowledge that you have read, understood, and agree to be bound by this Agreement. If you are entering into this Agreement on behalf of a business or other legal entity, you represent that you have the authority to bind such entity to this Agreement. If you do not agree to these terms, do not access or use the Platform.
        </Typography>
      </Paper>

      {/* Section 1 */}
      <Box id="definitions" sx={{ scrollMarginTop: LEGAL_STICKY_BAR_HEIGHT + 16 }}>
        <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700, mb: 3 }}>
          1. Definitions
        </Typography>
        <Box component="ul" sx={{ ...legalBodyTextSx, mb: 3 }}>
          <li><strong>"Account"</strong> means the unique user account created by or for you to access and use the Platform.</li>
          <li><strong>"Authorized Users"</strong> means the individuals authorized by the subscribing entity to access the Platform under a subscription plan.</li>
          <li><strong>"Business Data"</strong> means all data, files, content, and information that you or your Authorized Users upload, submit, transmit, or otherwise make available through the Platform, including data imported from Third-Party Integrations.</li>
          <li><strong>"Confidential Information"</strong> means any non-public information disclosed by either party, including but not limited to Business Data, trade secrets, financial information, technical specifications, and business strategies.</li>
          <li><strong>"Documentation"</strong> means any user guides, help articles, API documentation, and other materials made available by the Company describing the features and functionality of the Platform.</li>
          <li><strong>"Intellectual Property"</strong> means all patents, copyrights, trademarks, trade secrets, and other proprietary rights.</li>
          <li><strong>"Platform"</strong> means the MyLojic software-as-a-service analytics platform, including all features, tools, APIs, dashboards, AI-powered analytics capabilities, and related services provided by the Company.</li>
          <li><strong>"Subscription Plan"</strong> means the specific pricing tier and feature set selected by you, as described on the Company's website or in a separate order form.</li>
          <li><strong>"Third-Party Integrations"</strong> means external applications and services that connect to the Platform, including but not limited to QuickBooks, Shopify, Gusto, Xero, and other business software.</li>
        </Box>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Section 2 */}
      <Box id="license" sx={{ scrollMarginTop: LEGAL_STICKY_BAR_HEIGHT + 16 }}>
        <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700, mb: 3 }}>
          2. License Grant
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          2.1 Grant of License
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          Subject to your compliance with this Agreement and payment of all applicable fees, the Company grants you a limited, non-exclusive, non-transferable, revocable license to access and use the Platform solely for your internal business purposes during the term of your subscription.
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          2.2 Restrictions
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          You shall not:
        </Typography>
        <Box component="ul" sx={{ ...legalBodyTextSx, mb: 3 }}>
          <li>(a) Copy, modify, adapt, translate, reverse engineer, decompile, or disassemble any portion of the Platform;</li>
          <li>(b) Create derivative works based on the Platform;</li>
          <li>(c) Sublicense, lease, rent, loan, sell, or otherwise distribute the Platform to any third party;</li>
          <li>(d) Use the Platform to develop a competing product or service;</li>
          <li>(e) Remove or alter any proprietary notices, labels, or marks on the Platform;</li>
          <li>(f) Use the Platform for any unlawful purpose or in violation of any applicable laws or regulations;</li>
          <li>(g) Attempt to gain unauthorized access to the Platform, other users' accounts, or any related systems or networks;</li>
          <li>(h) Interfere with, disrupt, or attempt to circumvent the security or integrity of the Platform.</li>
        </Box>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Section 3 */}
      <Box id="accounts" sx={{ scrollMarginTop: LEGAL_STICKY_BAR_HEIGHT + 16 }}>
        <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700, mb: 3 }}>
          3. Account Registration and Security
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          3.1 Account Creation
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          To use the Platform, you must register for an account by providing accurate, current, and complete information. You agree to update your information as necessary to keep it accurate and complete.
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          3.2 Account Security
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          You are responsible for maintaining the confidentiality of your account credentials, including your password and any multi-factor authentication methods. You agree to immediately notify the Company of any unauthorized use of your account or any other breach of security.
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          3.3 Account Responsibility
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          You are solely responsible for all activities that occur under your account, regardless of whether such activities are undertaken by you, your Authorized Users, or any third party. The Company is not liable for any loss or damage arising from your failure to maintain the security of your account.
        </Typography>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Section 4 */}
      <Box id="payment" sx={{ scrollMarginTop: LEGAL_STICKY_BAR_HEIGHT + 16 }}>
        <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700, mb: 3 }}>
          4. Subscription and Payment
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          4.1 Subscription Plans
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          Access to the Platform is provided on a subscription basis. The features, limitations, and pricing of each Subscription Plan are described on the Company's website or in a separate order form. The Company reserves the right to modify Subscription Plans, features, and pricing upon reasonable advance notice.
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          4.2 Payment and Non-Refundability
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          You agree to pay all fees associated with your Subscription Plan in accordance with the billing terms presented at the time of purchase. Any initial setup, implementation, or onboarding fees are fully earned by the Company upon payment and are non-refundable, as they cover the immediate costs of account provisioning and resource allocation. Monthly subscription fees are non-refundable for the current billing cycle once processed.
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          4.3 Taxes
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          All fees are exclusive of applicable taxes, levies, or duties. You are responsible for paying all such taxes, excluding taxes based on the Company's net income.
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          4.4 Overdue Payments
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          If any payment is overdue, the Company may suspend or terminate your access to the Platform upon written notice. Overdue amounts may accrue interest at the lesser of 1.5% per month or the maximum rate permitted by applicable law.
        </Typography>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Section 5 */}
      <Box id="data-rights" sx={{ scrollMarginTop: LEGAL_STICKY_BAR_HEIGHT + 16 }}>
        <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700, mb: 3 }}>
          5. Business Data and Data Rights
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          5.1 Ownership of Business Data
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          You retain all rights, title, and interest in and to your Business Data. The Company does not claim any ownership rights in your Business Data.
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          5.2 License to Business Data
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          By submitting Business Data to the Platform, you grant the Company a limited, non-exclusive license to access, use, process, store, and display your Business Data solely for the purpose of providing, maintaining, and improving the Platform and related services. This license terminates upon deletion of your Business Data or termination of your account.
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          5.3 Data Processing
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          The Platform processes your Business Data through a multi-layer architecture (Bronze, Silver, and Gold data layers) to provide analytics, insights, and AI-powered recommendations. You acknowledge and consent to this processing as described in our Privacy Policy.
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          5.4 Data Isolation
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          Your Business Data is logically isolated from other users' data through the Platform's multi-tenant architecture. Tenant data is managed via attribute-based access controls and Row-Level Security (RLS) to ensure that each tenant can only access their own specific data subset, preventing unauthorized cross-tenant access.
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          5.5 Data Portability
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          You may export your Business Data from the Platform at any time through the Platform's export functionality or by contacting the Company's support team. The Company will provide your data in a commonly used, machine-readable format.
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          5.6 Data Deletion
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          Upon termination of your account, the Company will delete your Business Data within thirty (30) days, except where retention is required by applicable law or regulation. You may request earlier deletion by contacting the Company's support team.
        </Typography>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Section 6 */}
      <Box id="integrations" sx={{ scrollMarginTop: LEGAL_STICKY_BAR_HEIGHT + 16 }}>
        <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700, mb: 3 }}>
          6. Third-Party Integrations
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          6.1 Connector Services
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          The Platform provides integrations with Third-Party Integrations such as QuickBooks, Shopify, Gusto, and other business software. These integrations allow you to import data from and, where applicable, export data to these third-party services.
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          6.2 Third-Party Terms
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          Your use of Third-Party Integrations is subject to the terms and conditions of those third-party providers. The Company is not responsible for the availability, accuracy, or functionality of Third-Party Integrations.
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          6.3 Authorization
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          By connecting a Third-Party Integration to the Platform, you authorize the Company to access, retrieve, and process data from that third-party service on your behalf. You represent that you have the necessary rights and permissions to share such data with the Platform.
        </Typography>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Section 7 */}
      <Box id="ai-features" sx={{ scrollMarginTop: LEGAL_STICKY_BAR_HEIGHT + 16 }}>
        <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700, mb: 3 }}>
          7. AI-Powered Features and Analytics
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          7.1 AI Services
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          The Platform uses artificial intelligence and machine learning technologies, including services provided by Microsoft Azure OpenAI, to analyze your Business Data, generate insights, detect patterns, suggest data transformations, and provide business recommendations.
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          7.2 AI Limitations
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          AI-generated insights and recommendations are provided for informational purposes only and should not be considered professional financial, legal, tax, or business advice. You are solely responsible for any decisions made based on AI-generated content. The Company does not guarantee the accuracy, completeness, or reliability of AI-generated outputs.
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          7.3 Data Transformation Approvals
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          The Platform may propose data transformations and categorizations based on AI analysis. All proposed transformations that could materially alter your Business Data require your explicit approval before execution. The Company will not apply material changes to your data without your consent.
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          7.4 Token Usage
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          AI-powered features consume computational tokens. Your Subscription Plan includes a daily token allowance. The Company may throttle or restrict AI features if your usage exceeds your allocated allowance. Additional token capacity may be available for purchase.
        </Typography>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Section 8 */}
      <Box id="ip" sx={{ scrollMarginTop: LEGAL_STICKY_BAR_HEIGHT + 16 }}>
        <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700, mb: 3 }}>
          8. Intellectual Property
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          8.1 Company IP
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          The Platform, including all software, algorithms, designs, architectures, documentation, trademarks, trade names, logos, and all related Intellectual Property, is and shall remain the exclusive property of the Company. Nothing in this Agreement grants you any right, title, or interest in the Company's Intellectual Property except for the limited license expressly stated herein.
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          8.2 Feedback
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          If you provide the Company with any suggestions, ideas, enhancement requests, feedback, or recommendations regarding the Platform ("Feedback"), you hereby grant the Company a perpetual, irrevocable, royalty-free, worldwide license to use, modify, and incorporate such Feedback without any obligation to you.
        </Typography>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Section 9 */}
      <Box id="confidentiality" sx={{ scrollMarginTop: LEGAL_STICKY_BAR_HEIGHT + 16 }}>
        <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700, mb: 3 }}>
          9. Confidentiality
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          Each party agrees to maintain the confidentiality of the other party's Confidential Information and to use such information only for the purposes contemplated by this Agreement. Neither party shall disclose Confidential Information to any third party without the prior written consent of the disclosing party, except as required by law, regulation, or court order, or to employees, contractors, or agents who need to know such information and are bound by confidentiality obligations at least as protective as those contained herein.
        </Typography>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Section 10 */}
      <Box id="warranties" sx={{ scrollMarginTop: LEGAL_STICKY_BAR_HEIGHT + 16 }}>
        <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700, mb: 3 }}>
          10. Warranties and Disclaimers
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          10.1 Company Warranties
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          The Company warrants that: (a) it has the right and authority to enter into this Agreement and grant the licenses described herein; (b) the Platform will perform materially in accordance with the Documentation during the subscription term; and (c) the Company will use commercially reasonable security measures to protect your Business Data.
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          10.2 Disclaimer
        </Typography>
        <Paper
          elevation={0}
          sx={{
            p: 2, mb: 3,
            border: '1px solid', borderColor: 'divider', borderRadius: 1,
            bgcolor: 'action.hover',
          }}
        >
          <Typography sx={{ ...legalBodyTextSx, fontWeight: 500 }}>
            EXCEPT AS EXPRESSLY PROVIDED IN THIS AGREEMENT, THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE." THE COMPANY DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. THE COMPANY DOES NOT WARRANT THAT THE PLATFORM WILL BE UNINTERRUPTED, ERROR-FREE, SECURE, OR FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS.
          </Typography>
        </Paper>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Section 11 */}
      <Box id="liability" sx={{ scrollMarginTop: LEGAL_STICKY_BAR_HEIGHT + 16 }}>
        <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700, mb: 3 }}>
          11. Limitation of Liability
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          11.1 Exclusion of Certain Damages
        </Typography>
        <Paper
          elevation={0}
          sx={{
            p: 2, mb: 3,
            border: '1px solid', borderColor: 'divider', borderRadius: 1,
            bgcolor: 'action.hover',
          }}
        >
          <Typography sx={{ ...legalBodyTextSx, fontWeight: 500 }}>
            TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL THE COMPANY BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, PUNITIVE, OR EXEMPLARY DAMAGES, INCLUDING BUT NOT LIMITED TO DAMAGES FOR LOSS OF PROFITS, GOODWILL, DATA, BUSINESS OPPORTUNITIES, OR OTHER INTANGIBLE LOSSES, EVEN IF THE COMPANY HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
          </Typography>
        </Paper>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          11.2 Cap on Liability
        </Typography>
        <Paper
          elevation={0}
          sx={{
            p: 2, mb: 3,
            border: '1px solid', borderColor: 'divider', borderRadius: 1,
            bgcolor: 'action.hover',
          }}
        >
          <Typography sx={{ ...legalBodyTextSx, fontWeight: 500 }}>
            THE COMPANY'S TOTAL CUMULATIVE LIABILITY ARISING OUT OF OR RELATED TO THIS AGREEMENT SHALL NOT EXCEED THE TOTAL AMOUNT OF FEES ACTUALLY PAID BY YOU TO THE COMPANY DURING THE TWELVE (12) MONTHS IMMEDIATELY PRECEDING THE EVENT GIVING RISE TO THE CLAIM. IF YOU HAVE BEEN A USER FOR FEWER THAN TWELVE (12) MONTHS, THE LIABILITY SHALL BE LIMITED TO THE TOTAL SUM ACTUALLY PAID DURING YOUR PERIOD OF ACTIVE SUBSCRIPTION.
          </Typography>
        </Paper>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          11.3 Exceptions
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          The limitations in this Section 11 do not apply to: (a) the Company's breach of its confidentiality obligations; (b) the Company's willful misconduct or gross negligence; or (c) any liability that cannot be limited under applicable law.
        </Typography>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Section 12 */}
      <Box id="indemnification" sx={{ scrollMarginTop: LEGAL_STICKY_BAR_HEIGHT + 16 }}>
        <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700, mb: 3 }}>
          12. Indemnification
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          12.1 Your Indemnification
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          You agree to indemnify, defend, and hold harmless the Company and its officers, directors, employees, agents, and affiliates from and against any and all claims, damages, losses, liabilities, costs, and expenses (including reasonable attorneys' fees) arising out of or related to: (a) your use of the Platform; (b) your violation of this Agreement; (c) your violation of any applicable law or regulation; (d) your Business Data; or (e) your infringement of any third-party rights.
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          12.2 Company Indemnification
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          The Company will indemnify and defend you from claims that the Platform infringes a valid United States patent, copyright, or trademark, provided you promptly notify the Company of such claim and cooperate in the defense.
        </Typography>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Section 13 */}
      <Box id="termination" sx={{ scrollMarginTop: LEGAL_STICKY_BAR_HEIGHT + 16 }}>
        <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700, mb: 3 }}>
          13. Term and Termination
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          13.1 Term
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          This Agreement is effective as of the date you first access or use the Platform and continues until terminated in accordance with this Section 13.
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          13.2 Termination by You
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          You may terminate this Agreement at any time by canceling your subscription through your account settings or by contacting the Company's support team. Termination does not entitle you to a refund of any prepaid fees.
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          13.3 Termination by Company
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          The Company may terminate or suspend your access to the Platform immediately, without prior notice, if: (a) you breach any provision of this Agreement; (b) you fail to pay any fees when due; (c) the Company is required to do so by law; or (d) the Company ceases to offer the Platform.
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          13.4 Effects of Termination
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          Upon termination: (a) all rights and licenses granted to you under this Agreement will immediately cease; (b) you must stop using the Platform; (c) the Company will delete your Business Data within thirty (30) days unless retention is required by law; and (d) Sections 5.1, 8, 9, 10.2, 11, 12, 14, and 15 shall survive termination.
        </Typography>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Section 14 */}
      <Box id="dispute" sx={{ scrollMarginTop: LEGAL_STICKY_BAR_HEIGHT + 16 }}>
        <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700, mb: 3 }}>
          14. Dispute Resolution
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          14.1 Governing Law
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          This Agreement shall be governed by and construed in accordance with the laws of the State of Texas, without regard to its conflict of law principles.
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          14.2 Informal Resolution
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          Before initiating any formal dispute resolution proceedings, the parties agree to attempt to resolve any dispute informally by contacting each other and negotiating in good faith for a period of at least thirty (30) days.
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          14.3 Arbitration
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          Any dispute, controversy, or claim arising out of or relating to this Agreement that is not resolved through informal negotiation shall be resolved by binding arbitration administered by the American Arbitration Association ("AAA") in accordance with its Commercial Arbitration Rules. The arbitration shall take place in Austin, Texas. The arbitrator's decision shall be final and binding and may be entered in any court of competent jurisdiction.
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          14.4 Class Action Waiver
        </Typography>
        <Paper
          elevation={0}
          sx={{
            p: 2, mb: 3,
            border: '1px solid', borderColor: 'divider', borderRadius: 1,
            bgcolor: 'action.hover',
          }}
        >
          <Typography sx={{ ...legalBodyTextSx, fontWeight: 500 }}>
            YOU AND THE COMPANY AGREE THAT EACH PARTY MAY BRING CLAIMS AGAINST THE OTHER ONLY IN YOUR OR ITS INDIVIDUAL CAPACITY AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS, CONSOLIDATED, OR REPRESENTATIVE PROCEEDING.
          </Typography>
        </Paper>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Section 15 */}
      <Box id="general" sx={{ scrollMarginTop: LEGAL_STICKY_BAR_HEIGHT + 16 }}>
        <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700, mb: 3 }}>
          15. General Provisions
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          15.1 Entire Agreement
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          This Agreement, together with the Privacy Policy and any order forms or subscription agreements, constitutes the entire agreement between you and the Company with respect to the subject matter hereof and supersedes all prior and contemporaneous agreements, understandings, negotiations, and discussions.
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          15.2 Amendments
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          The Company may modify this Agreement at any time by posting the revised version on the Platform or by providing you with notice. Your continued use of the Platform after the effective date of any modification constitutes your acceptance of the modified Agreement. If you do not agree to the modified terms, you must stop using the Platform.
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          15.3 Severability
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          If any provision of this Agreement is held to be invalid, illegal, or unenforceable, the remaining provisions shall continue in full force and effect.
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          15.4 Waiver
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          The failure of either party to enforce any right or provision of this Agreement shall not constitute a waiver of such right or provision.
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          15.5 Assignment
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          You may not assign or transfer this Agreement or any rights or obligations hereunder without the Company's prior written consent. The Company may assign this Agreement in connection with a merger, acquisition, or sale of all or substantially all of its assets.
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          15.6 Force Majeure
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          Neither party shall be liable for any failure or delay in performance due to causes beyond its reasonable control, including but not limited to acts of God, natural disasters, war, terrorism, pandemics, government actions, power failures, internet disruptions, or third-party service provider outages.
        </Typography>

        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          15.7 Notices
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          All notices under this Agreement shall be in writing and shall be deemed given when delivered by email. Notices to the Company shall be sent to{' '}
          <Link href="mailto:legal@mylojic.com" sx={{ color: 'primary.main' }}>legal@mylojic.com</Link>.
          Notices to you shall be sent to the email address associated with your account.
        </Typography>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Section 16 */}
      <Box id="contact" sx={{ scrollMarginTop: LEGAL_STICKY_BAR_HEIGHT + 16 }}>
        <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700, mb: 3 }}>
          16. Contact Information
        </Typography>
        <Typography paragraph sx={legalBodyTextSx}>
          If you have any questions about this Agreement, please contact us:
        </Typography>
        <Paper elevation={1} sx={{ p: 3, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', mb: 3 }}>
          <Typography sx={{ ...legalBodyTextSx, fontWeight: 600, mb: 1 }}>
            Data Edge Services LLC
          </Typography>
          <Typography sx={{ ...legalBodyTextSx, mb: 1 }}>
            doing business as MyLojic
          </Typography>
          <Typography sx={legalBodyTextSx}>
            Email:{' '}
            <Link href="mailto:legal@mylojic.com" sx={{ color: 'primary.main' }}>
              legal@mylojic.com
            </Link>
          </Typography>
          <Typography sx={legalBodyTextSx}>
            Website: www.mylojic.com
          </Typography>
        </Paper>

        {/* Final Notice */}
        <Paper elevation={2} sx={{ p: 3, bgcolor: 'primary.main', mt: 4 }}>
          <Typography sx={{ color: 'white', fontWeight: 600, textAlign: 'center' }}>
            BY USING THE MYLOJIC PLATFORM, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THIS END USER LICENSE AGREEMENT.
          </Typography>
        </Paper>
      </Box>

    </Container>
  </LegalPageShell>
);

export default Eula;
